import React, { useEffect, useRef, useState } from 'react'
import Breadcrumbs from '../../components/Breadcrumbs/Breadcrumbs';
import { t } from 'i18next';
import useStore from '../../store/useStore';
import SuccessAlert from '../../components/Alerts/SuccessAlert';
import FailAlert from '../../components/Alerts/FailAlert';
import { useMutation, useQuery } from '@apollo/client';
import { ALL_MARKETERS, Marketer, UpdateMarketer } from '../../graphql';
import { useParams } from 'react-router-dom';
import { FetchMarketer } from '../../types';
import LoadingWidget from '../../components/LoadingWidget/LoadingWidget';
import PermissionsError from '../../components/Alerts/PermissionsError';

const EditMarketer = () => {
  const { marketerId } = useParams();

  const storeData = useStore((state: any) => state.userData.store);
  const userData = useStore((state: any) => state.userData);

  // i had to use a main state for control loading because of ui issues
  const [initialLoading, setInitialLoading] = useState(true);

  const [permissions, setPermissions] = useState({
    edit: false,
  })

  // Permission configuration
  useEffect(() => {
    if (Object.keys(userData).length !== 0) {
      // admin
      if (userData.permissions.length === 0 && userData.role === "admin") {
        setPermissions({
          edit: true,
        })

        return
      }

      const filtered = userData?.permissions?.filter((perm: { name: string }) => perm.name === "marketing")

      let editPermission = filtered[0]?.permissions.includes("edit")

      setPermissions({
        edit: editPermission || false
      })
    }
  }, [userData])

  const [success, setSuccess] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [fail, setFail] = useState(false);
  const [failMsg, setFailMsg] = useState("");

  const [typeDrop, setTypeDrop] = useState(false);

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");

  const [otherInfo, setOtherInfo] = useState("");
  const [marketingMethod, setMarketingMethod] = useState("");
  const [commisionType, setCommisionType] = useState("");

  const [value, setValue] = useState<number>(0);

  const TypeRef = useRef<any>();

  const { data: marketerData, loading } = useQuery<FetchMarketer>(Marketer, {
    variables: { marketerId: marketerId }
  });

  useEffect(() => {
    if (marketerData !== undefined) {
      setName(marketerData.marketer.name)
      setPhone(marketerData.marketer.phone)
      setEmail(marketerData.marketer.email)
      setOtherInfo(marketerData.marketer.otherInfo)
      setMarketingMethod(marketerData.marketer.marketingMethod)
      setCommisionType(marketerData.marketer.commissionType)
      setValue(marketerData.marketer.value)

      setInitialLoading(false)
    }
  }, [marketerData])

  // Create
  const [updateMarketer, { error }] = useMutation(UpdateMarketer, {
    refetchQueries: [{
      query: ALL_MARKETERS,
      variables: { idStore: storeData?.id }
    }]
  });

  useEffect(() => {
    document.body.addEventListener("click", (e) => {
      if (TypeRef.current) {
        if (TypeRef.current.contains(e.target as Node)) {
          return
        }

        // else close the dropdown
        setTypeDrop(false);
      }
    })
  }, [])

  const submit = () => {
    if (name === "") {
      setFailMsg(t('emptyClientNameAlert'));
      setFail(true);
      return
    }

    if (email === "") {
      setFailMsg(t('emptyEmailAlert'));
      setFail(true);
      return
    }

    if (phone === "") {
      setFailMsg(t('emptyPhoneAlert'));
      setFail(true);
      return
    }

    if (value === 0) {
      setFailMsg(t('emptyValue'));
      setFail(true);
      return
    }

    updateMarketer({
      variables: {
        updateMarketerId: marketerId,
        content: {
          email: email,
          name: name,
          phone: phone,
          otherInfo: otherInfo,
          value: value,
          idStore: storeData.id
        }
      }
    })

    if (!error) {
      setSuccessMsg(t('marketerUpdateSuccess'));
      setSuccess(true);
    }
  }

  return (
    <section className='mx-3 my-4 md:mx-7'>

      {success && <SuccessAlert success={success} setSuccess={setSuccess} successMsg={successMsg} />}

      {fail && <FailAlert fail={fail} setFail={setFail} failMsg={failMsg} />}

      <div className='flex flex-col-reverse md:flex-row md:justify-between md:items-center'>
        <div>
          <h3 className='text-2xl font-semibold'>{t("Edit Marketer")}</h3>
        </div>
        <div>
          <Breadcrumbs />
        </div>
      </div>

      {
        initialLoading ?
          <LoadingWidget />
          :
          permissions.edit === false ?
            <PermissionsError />
            :
            <form className='relative px-5 py-2 my-2 bg-white rounded-md'>
              {
                loading &&
                <div className='absolute left-0 z-30 flex items-center justify-center w-full h-full py-10 bg-white/70'>
                  <svg aria-hidden="true" className="w-8 h-8 mr-2 text-gray-200 animate-spin fill-blue-600" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor" />
                    <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill" />
                  </svg>
                </div>
              }
              <div className='grid grid-cols-2 gap-4 mb-2'>
                <div>
                  <label htmlFor="name" className="block mb-2 text-sm font-medium text-gray-900">{t('name')}</label>
                  <input type="text" value={name} onChange={(e) => setName(e.target.value)} id="name" className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary focus:border-primary outline-none block w-full p-2.5" placeholder={t('name')} required />
                </div>
                <div>
                  <label htmlFor="phone" className="block mb-2 text-sm font-medium text-gray-900">{t('phone')}</label>
                  <input type="text" id="phone" value={phone} onChange={(e) => setPhone(e.target.value)} className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary focus:border-primary outline-none block w-full p-2.5" placeholder={t('phone')} required />
                </div>
              </div>

              <div>
                <div className="mb-2">
                  <label htmlFor="email" className="block mb-2 text-sm font-medium text-gray-900">{t('email')}</label>
                  <input type="text" id="email" value={email} onChange={(e) => setEmail(e.target.value)} className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary focus:border-primary outline-none block w-full p-2.5" placeholder={t('email')} required />
                </div>
              </div>

              <div>
                <div className="mb-2">
                  <label htmlFor="notice" className="block mb-2 text-sm font-medium text-gray-900">{t('moreInformation')}</label>
                  <textarea id="notice" value={otherInfo} onChange={(e) => setOtherInfo(e.target.value)} className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary focus:border-primary outline-none block w-full p-2.5" placeholder={t('moreInformation')} required />
                </div>
              </div>

              <div className='my-6'>
                <h3>{t('marketerCommission')}</h3>
              </div>


              <div>
                <div className="mt-2">
                  <label htmlFor="value" className="block mb-2 text-sm font-medium text-gray-900">{t('commitionValue')} %</label>
                  <input type="number" min={0} value={value} onChange={(e) => setValue(parseInt(e.target.value))} id="value" className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary focus:border-primary outline-none block w-full p-2.5" placeholder='Value' required />
                </div>
              </div>

              <div className='flex justify-end pt-5'>
                <button onClick={(e) => { e.preventDefault(); submit() }} className='inline-flex items-center justify-center w-full px-4 py-2.5 overflow-hidden text-sm text-white transition-colors duration-300 bg-primary rounded-lg shadow sm:w-auto my-2 md:my-0 hover:bg-primaryHover'>
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20 2H8c-1.103 0-2 .897-2 2v12c0 1.103.897 2 2 2h12c1.103 0 2-.897 2-2V4c0-1.103-.897-2-2-2zM8 16V4h12l.002 12H8z"></path>
                    <path d="M4 8H2v12c0 1.103.897 2 2 2h12v-2H4V8zm11-2h-2v3h-3v2h3v3h2v-3h3V9h-3z"></path>
                  </svg>

                  <span className="mx-2">
                    {t('save')}
                  </span>
                </button>
              </div>
            </form>
      }
    </section>
  )
}

export default EditMarketer